/**
 * Workforce — Personaluebersicht mit Team-Ansicht, Abwesenheiten und Verfuegbarkeit.
 *
 * Features:
 * - Team-Tab: MA-Liste mit Rolle, Stunden/Woche, Arbeitstage
 * - Abwesenheits-Tab: Urlaub, Krank, ZA erfassen und verwalten
 * - Kalender-Ansicht der Abwesenheiten pro Monat
 * - Ring-Diagramm fuer Verfuegbarkeits-Quote
 *
 * @page /workforce
 * @roles admin, manager
 * @api /api/mitarbeiter, /api/verfuegbarkeit
 * @i18n workforce
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import useTabParam from '../hooks/useTabParam';
import { PageHeader, Badge } from '../components/ui';
import Ring from '../components/Ring';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ───────────────────────────────────────────────────────

function api(path, opts = {}) {
  return fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then((r) => r.json());
}

const rolleColor = {
  picker: 'bg-primary-500/15 text-primary-400 border-primary-500/20',
  packer: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  both: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
};
const tage = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const tageKeys = ['arbeitstage_mo', 'arbeitstage_di', 'arbeitstage_mi', 'arbeitstage_do', 'arbeitstage_fr', 'arbeitstage_sa', 'arbeitstage_so'];

const absTyp = {
  urlaub:  { labelKey: 'workforce.abs_urlaub',  color: '#338edc', bg: 'bg-primary-500/20 text-primary-300',   icon: 'U' },
  krank:   { labelKey: 'workforce.abs_krank',    color: '#ef4444', bg: 'bg-red-500/20 text-red-300',      icon: 'K' },
  za:      { labelKey: 'workforce.abs_za',       color: '#eab308', bg: 'bg-yellow-500/20 text-yellow-300', icon: 'Z' },
  sonstig: { labelKey: 'workforce.abs_sonstig',  color: '#a855f7', bg: 'bg-purple-500/20 text-purple-300', icon: 'S' },
};

function arbeitstageCount(ma) { return tageKeys.filter((k) => ma[k]).length; }
function hProTag(ma) { const at = arbeitstageCount(ma); return at ? (Number(ma.wochenstunden_soll) / at).toFixed(1) : '0'; }
function dateStr(d) { return d.toISOString().slice(0, 10); }
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

// ─── Modal ─────────────────────────────────────────────────────────

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-elevated border border-line-hover rounded-card w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl shadow-card">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 1: TEAM
// ═══════════════════════════════════════════════════════════════════

function TeamTab({ mitarbeiter, absences, selectedMa, setSelectedMa }) {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [filterRolle, setFilterRolle] = useState('');
  const [view, setView] = useState('card');

  const filtered = useMemo(() => {
    let d = mitarbeiter;
    if (search) d = d.filter((m) => m.name.toLowerCase().includes(search.toLowerCase()) || m.kuerzel.toLowerCase().includes(search.toLowerCase()));
    if (filterRolle) d = d.filter((m) => m.rolle === filterRolle);
    return d;
  }, [mitarbeiter, search, filterRolle]);

  const absCount = (maId) => absences.filter((a) => a.mitarbeiter_id === maId).length;

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex-1 min-w-[200px] relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('workforce.search_employee')}
            className="w-full bg-surface-inset border border-line-hover rounded-lg pl-9 pr-3 py-2 text-sm text-content placeholder-content-tertiary focus:outline-none focus:border-line-hover" />
        </div>
        <div className="flex bg-surface-inset rounded-button p-0.5 border border-line-hover">
          {['', 'picker', 'packer', 'both'].map((r) => (
            <button key={r} onClick={() => setFilterRolle(r)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${filterRolle === r ? 'bg-surface-active text-content' : 'text-[var(--text-secondary)] hover:text-content-secondary'}`}
            >{r ? t('workforce.rolle_' + r) : t('workforce.filter_all')}</button>
          ))}
        </div>
        <div className="flex bg-surface-inset rounded-button p-0.5 border border-line-hover">
          <button onClick={() => setView('card')} className={`px-2.5 py-1.5 rounded text-xs transition-colors ${view === 'card' ? 'bg-surface-active text-content' : 'text-content-secondary'}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          </button>
          <button onClick={() => setView('list')} className={`px-2.5 py-1.5 rounded text-xs transition-colors ${view === 'list' ? 'bg-surface-active text-content' : 'text-content-secondary'}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
          </button>
        </div>
      </div>

      {/* Card View */}
      {view === 'card' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
          {filtered.map((ma) => {
            const isSelected = selectedMa === ma.id;
            const ac = absCount(ma.id);
            return (
              <button key={ma.id} onClick={() => setSelectedMa(isSelected ? null : ma.id)}
                className={`text-left bg-surface-card border rounded-card p-4 transition-all shadow-card ${isSelected ? 'border-amber-500/40 ring-1 ring-amber-500/20' : 'border-line hover:border-line-hover'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-content text-sm">{ma.name}</div>
                    <div className="text-xs text-[var(--text-secondary)] font-mono">{ma.kuerzel}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${rolleColor[ma.rolle]}`}>{t('workforce.rolle_' + ma.rolle)}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div><div className="text-[10px] text-content-secondary uppercase">{t('workforce.h_per_week')}</div><div className="font-mono text-sm font-bold">{Number(ma.wochenstunden_soll)}</div></div>
                  <div><div className="text-[10px] text-content-secondary uppercase">{t('workforce.h_per_day')}</div><div className="font-mono text-sm font-bold">{hProTag(ma)}</div></div>
                  <div><div className="text-[10px] text-content-secondary uppercase">{t('workforce.max_overtime')}</div><div className="font-mono text-sm font-bold">{Number(ma.max_ueberstunden_woche)}</div></div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {tage.map((tag, i) => (
                    <span key={tag} className={`flex-1 h-6 flex items-center justify-center rounded text-[10px] font-bold ${ma[tageKeys[i]] ? 'bg-surface-active text-content' : 'bg-surface-card text-content-tertiary'}`}>{tag}</span>
                  ))}
                </div>
                {ac > 0 && (
                  <div className="text-xs text-amber-400/80 font-medium">{t('workforce.absences_count', { n: ac })}</div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_name')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_short')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_role')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_h_week')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_h_day')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_working_days')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_max_overtime')}</th>
                <th className="px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('workforce.col_absences')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ma) => {
                const isSelected = selectedMa === ma.id;
                return (
                  <tr key={ma.id} onClick={() => setSelectedMa(isSelected ? null : ma.id)}
                    className={`border-b border-line cursor-pointer transition-colors ${isSelected ? 'bg-amber-500/5' : 'hover:bg-surface-card'}`}
                  >
                    <td className="px-4 py-3 font-medium text-content">{ma.name}</td>
                    <td className="px-4 py-3 font-mono text-content-tertiary">{ma.kuerzel}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${rolleColor[ma.rolle]}`}>{t('workforce.rolle_' + ma.rolle)}</span></td>
                    <td className="px-4 py-3 font-mono text-content-secondary">{Number(ma.wochenstunden_soll)}</td>
                    <td className="px-4 py-3 font-mono text-content-secondary">{hProTag(ma)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-0.5">
                        {tage.map((tag, i) => (
                          <span key={tag} className={`w-5 h-5 flex items-center justify-center rounded text-[9px] font-bold ${ma[tageKeys[i]] ? 'bg-surface-active text-content' : 'text-content-tertiary'}`}>{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-content-secondary">{Number(ma.max_ueberstunden_woche)}</td>
                    <td className="px-4 py-3 font-mono">{absCount(ma.id) > 0 ? <span className="text-amber-400">{absCount(ma.id)}</span> : <span className="text-content-tertiary">0</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedMa && (
        <div className="mt-4 text-xs text-[var(--text-secondary)]">
          {t('workforce.selected_info', { name: mitarbeiter.find((m) => m.id === selectedMa)?.name })}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 2: KALENDER
// ═══════════════════════════════════════════════════════════════════

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  let startDay = first.getDay();
  if (startDay === 0) startDay = 7; // Mo=1
  const days = [];
  // Pad before
  for (let i = 1; i < startDay; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  // Pad after
  while (days.length % 7 !== 0) days.push(null);
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

function getKW(date) {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

function AbsForm({ mitarbeiter, initial, onSave, onCancel }) {
  const { t } = useLanguage();
  const [f, setF] = useState(initial || { mitarbeiter_id: '', datum_von: '', datum_bis: '', typ: 'urlaub', notiz: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = (e) => { e.preventDefault(); onSave({ ...f, mitarbeiter_id: Number(f.mitarbeiter_id) }); };

  return (
    <form onSubmit={submit} className="space-y-4">
      <label className="block">
        <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('workforce.label_employee')}</span>
        <select value={f.mitarbeiter_id} onChange={(e) => set('mitarbeiter_id', e.target.value)} required
          className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-line-hover">
          <option value="">{t('workforce.placeholder_select')}</option>
          {mitarbeiter.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.kuerzel})</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('workforce.label_from')}</span>
          <input type="date" value={f.datum_von} onChange={(e) => set('datum_von', e.target.value)} required
            className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-line-hover" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('workforce.label_to')}</span>
          <input type="date" value={f.datum_bis} onChange={(e) => set('datum_bis', e.target.value)} required
            className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-line-hover" />
        </label>
      </div>
      <label className="block">
        <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('workforce.label_type')}</span>
        <div className="flex gap-2 mt-1.5">
          {Object.entries(absTyp).map(([key, typ]) => (
            <button key={key} type="button" onClick={() => set('typ', key)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${f.typ === key ? typ.bg + ' border-current' : 'bg-surface-inset text-[var(--text-secondary)] border-transparent hover:bg-surface-active'}`}
            >{t(typ.labelKey)}</button>
          ))}
        </div>
      </label>
      <label className="block">
        <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('workforce.label_note')}</span>
        <input type="text" value={f.notiz || ''} onChange={(e) => set('notiz', e.target.value)} placeholder={t('workforce.placeholder_optional')}
          className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content placeholder-content-tertiary focus:outline-none focus:border-line-hover" />
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover">{t('workforce.btn_cancel')}</button>
        <button type="submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500/20 text-amber-300 hover:bg-amber-500/30">{t('workforce.btn_save')}</button>
      </div>
    </form>
  );
}

function KalenderTab({ mitarbeiter, absences, loadAbsences, selectedMa, setSelectedMa }) {
  const { t, tMonth, locale } = useLanguage();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1); // 0-indexed, Feb=1
  const [modal, setModal] = useState(null); // null | 'new' | absObj
  const [clickedDate, setClickedDate] = useState(null);

  const weeks = useMemo(() => getMonthGrid(year, month), [year, month]);

  const filteredAbs = useMemo(() => {
    if (selectedMa) return absences.filter((a) => a.mitarbeiter_id === selectedMa);
    return absences;
  }, [absences, selectedMa]);

  function getAbsForDay(day) {
    if (!day) return [];
    const ds = dateStr(day);
    return filteredAbs.filter((a) => a.datum_von <= ds && a.datum_bis >= ds);
  }

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const save = async (a) => {
    if (a.id) {
      const { id, created_at, mitarbeiter_name, ...body } = a;
      await api(`/api/verfuegbarkeit/${id}`, { method: 'PUT', body });
    } else {
      await api('/api/verfuegbarkeit', { method: 'POST', body: a });
    }
    setModal(null);
    loadAbsences();
  };

  const remove = async (id) => {
    await api(`/api/verfuegbarkeit/${id}`, { method: 'DELETE' });
    loadAbsences();
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const dayAbs = getAbsForDay(day);
    if (dayAbs.length > 0) {
      setModal(dayAbs[0]);
    } else {
      setClickedDate(dateStr(day));
      setModal('new');
    }
  };

  const today = new Date();

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-surface-hover text-content-tertiary hover:text-content transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h3 className="text-lg font-bold min-w-[180px] text-center">{tMonth(month)} {year}</h3>
          <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-surface-hover text-content-tertiary hover:text-content transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
        <div className="flex items-center gap-3">
          {selectedMa && (
            <button onClick={() => setSelectedMa(null)} className="text-xs text-amber-400 hover:text-amber-300 transition-colors">
              {t('workforce.clear_filter', { name: mitarbeiter.find((m) => m.id === selectedMa)?.name })}
              <span className="ml-1">&times;</span>
            </button>
          )}
          <button onClick={() => { setClickedDate(null); setModal('new'); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active transition-colors flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            {t('workforce.absence_btn')}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-3 mb-4 text-xs">
        {Object.entries(absTyp).map(([key, typ]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: typ.color }} />
            <span className="text-content-tertiary">{t(typ.labelKey)}</span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        {/* Weekday headers */}
        <div className="grid grid-cols-[40px_repeat(7,1fr)] border-b border-line">
          <div className="px-2 py-2 text-[10px] font-medium text-content-tertiary uppercase text-center">KW</div>
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
            <div key={d} className={`px-2 py-2 text-[10px] font-medium uppercase text-center ${d === 'Sa' || d === 'So' ? 'text-content-tertiary' : 'text-content-secondary'}`}>{d}</div>
          ))}
        </div>

        {/* Weeks */}
        {weeks.map((week, wi) => {
          const kwDate = week.find((d) => d !== null);
          const kw = kwDate ? getKW(kwDate) : '';
          return (
            <div key={wi} className="grid grid-cols-[40px_repeat(7,1fr)] border-b border-line last:border-b-0">
              <div className="px-2 py-1 text-[10px] font-mono text-content-tertiary flex items-center justify-center">{kw}</div>
              {week.map((day, di) => {
                if (!day) return <div key={di} className="p-1 min-h-[64px]" />;
                const dayAbs = getAbsForDay(day);
                const isToday = isSameDay(day, today);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                return (
                  <div key={di} onClick={() => handleDayClick(day)}
                    className={`p-1 min-h-[64px] border-l border-line cursor-pointer transition-colors hover:bg-surface-card ${isWeekend ? 'bg-surface-inset' : ''}`}
                  >
                    <div className={`text-[11px] font-mono mb-0.5 ${isToday ? 'text-amber-400 font-bold' : 'text-[var(--text-secondary)]'}`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayAbs.slice(0, 3).map((a) => (
                        <div key={a.id} className="text-[9px] font-medium px-1 py-0.5 rounded truncate" style={{ backgroundColor: absTyp[a.typ]?.color + '25', color: absTyp[a.typ]?.color }}>
                          {a.mitarbeiter_name || mitarbeiter.find((m) => m.id === a.mitarbeiter_id)?.kuerzel || '?'}
                        </div>
                      ))}
                      {dayAbs.length > 3 && (
                        <div className="text-[9px] text-[var(--text-secondary)] pl-1">+{dayAbs.length - 3}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Absence list below calendar */}
      {filteredAbs.length > 0 && (
        <div className="mt-5">
          <h4 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">
            {selectedMa ? t('workforce.absences_for_ma', { name: mitarbeiter.find((m) => m.id === selectedMa)?.name }) : t('workforce.absences_for_month', { month: tMonth(month), year })}
          </h4>
          <div className="space-y-1.5">
            {filteredAbs.map((a) => {
              const typ = absTyp[a.typ] || absTyp.sonstig;
              return (
                <div key={a.id} className="flex items-center gap-3 bg-surface-card border border-line rounded-card px-3 py-2 shadow-card">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: typ.color }} />
                  <span className="text-sm font-medium text-content flex-shrink-0 w-32 truncate">{a.mitarbeiter_name}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${typ.bg}`}>{t(typ.labelKey)}</span>
                  <span className="text-xs font-mono text-content-tertiary flex-1">
                    {new Date(a.datum_von).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })}
                    {a.datum_von !== a.datum_bis && ` – ${new Date(a.datum_bis).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })}`}
                  </span>
                  {a.notiz && <span className="text-xs text-content-tertiary truncate max-w-[120px]">{a.notiz}</span>}
                  <button onClick={() => setModal(a)} className="p-1 rounded hover:bg-surface-active text-content-tertiary hover:text-content flex-shrink-0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button onClick={() => remove(a.id)} className="p-1 rounded hover:bg-red-500/20 text-content-tertiary hover:text-red-400 flex-shrink-0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal open={modal !== null} onClose={() => setModal(null)} title={modal && modal !== 'new' ? t('workforce.edit_absence') : t('workforce.new_absence')}>
        <AbsForm
          mitarbeiter={mitarbeiter}
          initial={modal !== 'new' ? modal : (clickedDate ? { mitarbeiter_id: selectedMa || '', datum_von: clickedDate, datum_bis: clickedDate, typ: 'urlaub', notiz: '' } : null)}
          onSave={save}
          onCancel={() => setModal(null)}
        />
      </Modal>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 3: KAPAZITÄT
// ═══════════════════════════════════════════════════════════════════

function KapazitaetTab({ mitarbeiter, absences }) {
  const { t, locale } = useLanguage();
  const [datum, setDatum] = useState(dateStr(new Date()));

  const selDate = new Date(datum + 'T00:00:00');
  const dayOfWeek = selDate.getDay(); // 0=So,1=Mo,...6=Sa
  const dayKeyMap = [6, 0, 1, 2, 3, 4, 5]; // JS day → tageKeys index
  const dayIdx = dayKeyMap[dayOfWeek];

  // Which MA are available on this day?
  const verfuegbar = useMemo(() => {
    return mitarbeiter.filter((ma) => {
      if (!ma[tageKeys[dayIdx]]) return false;
      // Check absences
      const ds = datum;
      const hasAbs = absences.some((a) => a.mitarbeiter_id === ma.id && a.datum_von <= ds && a.datum_bis >= ds);
      return !hasAbs;
    });
  }, [mitarbeiter, absences, datum, dayIdx]);

  const abwesend = useMemo(() => {
    return mitarbeiter.filter((ma) => {
      const ds = datum;
      return absences.some((a) => a.mitarbeiter_id === ma.id && a.datum_von <= ds && a.datum_bis >= ds);
    });
  }, [mitarbeiter, absences, datum]);

  const pickerAvail = verfuegbar.filter((m) => m.rolle === 'picker' || m.rolle === 'both');
  const packerAvail = verfuegbar.filter((m) => m.rolle === 'packer' || m.rolle === 'both');

  const pickH = pickerAvail.reduce((sum, m) => sum + Number(hProTag(m)), 0);
  const packH = packerAvail.reduce((sum, m) => sum + Number(hProTag(m)), 0);

  // Simulated demand (will come from forecast later)
  const pickBedarf = 36;
  const packBedarf = 42;

  const pickLuecke = pickBedarf - pickH;
  const packLuecke = packBedarf - packH;

  function Gauge({ label, bedarf, verfuegbar: verf, color }) {
    const pct = bedarf > 0 ? Math.min((verf / bedarf) * 100, 100) : 100;
    const ok = verf >= bedarf;
    return (
      <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-content-secondary">{label}</span>
          <Ring pct={pct} color={ok ? '#34d399' : '#f87171'} size={40} stroke={3} />
        </div>
        <div className="flex gap-3 mb-3">
          <div><div className="text-[10px] text-content-secondary uppercase">{t('workforce.demand')}</div><div className="font-mono text-lg font-bold text-content">{bedarf}h</div></div>
          <div><div className="text-[10px] text-content-secondary uppercase">{t('workforce.available')}</div><div className="font-mono text-lg font-bold text-content">{verf.toFixed(1)}h</div></div>
          <div><div className="text-[10px] text-content-secondary uppercase">{t('workforce.difference')}</div><div className="font-mono text-lg font-bold text-content">{verf >= bedarf ? '+' : ''}{(verf - bedarf).toFixed(1)}h</div></div>
        </div>
        <div className="h-2 bg-surface-inset rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: ok ? '#34d399' : '#f87171' }} />
        </div>
      </div>
    );
  }

  // Overtime candidates
  const uesCandidates = useMemo(() => {
    if (pickLuecke <= 0 && packLuecke <= 0) return [];
    const needed = pickLuecke > 0 ? 'picker' : 'packer';
    return verfuegbar
      .filter((m) => m.rolle === needed || m.rolle === 'both')
      .map((m) => ({ ...m, maxUes: Math.min(Number(m.max_ueberstunden_woche) / 5, 2) }))
      .filter((m) => m.maxUes > 0)
      .sort((a, b) => b.maxUes - a.maxUes);
  }, [verfuegbar, pickLuecke, packLuecke]);

  return (
    <>
      {/* Date picker */}
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm text-content-tertiary">{t('workforce.date_label')}</label>
        <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)}
          className="bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content font-mono focus:outline-none focus:border-line-hover" />
        <span className="text-sm text-[var(--text-secondary)]">
          {selDate.toLocaleDateString(locale, { weekday: 'long' })}
        </span>
      </div>

      {/* Gauges */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Gauge label="Picking" bedarf={pickBedarf} verfuegbar={pickH} color="#338edc" />
        <Gauge label="Packing" bedarf={packBedarf} verfuegbar={packH} color="#66ABE5" />
      </div>

      {/* Verfügbar / Abwesend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div>
          <h4 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('workforce.available_count', { n: verfuegbar.length })}</h4>
          <div className="space-y-1">
            {verfuegbar.map((m) => (
              <div key={m.id} className="flex items-center gap-2 bg-surface-card border border-line rounded-card px-3 py-2 shadow-card">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-sm font-medium text-content flex-1">{m.name}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${rolleColor[m.rolle]}`}>{t('workforce.rolle_' + m.rolle)}</span>
                <span className="text-xs font-mono text-content-tertiary">{hProTag(m)}h</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('workforce.absent_count', { n: abwesend.length })}</h4>
          {abwesend.length === 0 ? (
            <div className="text-sm text-content-tertiary py-4">{t('workforce.no_absences')}</div>
          ) : (
            <div className="space-y-1">
              {abwesend.map((m) => {
                const a = absences.find((a) => a.mitarbeiter_id === m.id && a.datum_von <= datum && a.datum_bis >= datum);
                const typ = a ? absTyp[a.typ] : absTyp.sonstig;
                return (
                  <div key={m.id} className="flex items-center gap-2 bg-surface-card border border-line rounded-card px-3 py-2 shadow-card">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: typ.color }} />
                    <span className="text-sm font-medium text-content-tertiary flex-1">{m.name}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${typ.bg}`}>{t(typ.labelKey)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Überstunden-Vorschlag */}
      {(pickLuecke > 0 || packLuecke > 0) && (
        <div className="bg-red-500/5 border border-red-500/15 rounded-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            <h4 className="text-sm font-bold text-red-400">{t('workforce.understaffed_title')}</h4>
          </div>
          <div className="text-xs text-content-tertiary mb-4">
            {pickLuecke > 0 && <span>{t('workforce.picking_gap', { n: pickLuecke.toFixed(1) })} </span>}
            {packLuecke > 0 && <span>{t('workforce.packing_gap', { n: packLuecke.toFixed(1) })}</span>}
          </div>
          {uesCandidates.length > 0 ? (
            <div className="space-y-2">
              {uesCandidates.map((m) => (
                <div key={m.id} className="flex items-center gap-3 bg-surface-card rounded-card px-3 py-2">
                  <span className="text-sm font-medium text-content flex-1">{m.name}</span>
                  <span className="text-xs font-mono text-content-tertiary">{t('workforce.max_overtime_hours', { n: m.maxUes.toFixed(1) })}</span>
                  <button className="px-3 py-1 rounded-lg text-xs font-semibold bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-colors">{t('workforce.accept')}</button>
                  <button className="px-3 py-1 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">{t('workforce.decline')}</button>
                </div>
              ))}
              <button className="w-full mt-2 py-2 rounded-lg text-xs font-semibold bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors">
                {t('workforce.accept_all')}
              </button>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-secondary)]">{t('workforce.no_overtime_candidates')}</div>
          )}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════

const tabsDef = [
  { key: 'team', labelKey: 'workforce.tab_team' },
  { key: 'kalender', labelKey: 'workforce.tab_calendar' },
  { key: 'kapazitaet', labelKey: 'workforce.tab_capacity' },
];

export default function Workforce() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('team');
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [selectedMa, setSelectedMa] = useState(null);

  const loadMa = useCallback(() => { api('/api/mitarbeiter').then(setMitarbeiter); }, []);
  const loadAbs = useCallback(() => {
    api('/api/verfuegbarkeit?jahr=2026&monat=2').then(setAbsences);
  }, []);

  useEffect(() => { loadMa(); loadAbs(); }, [loadMa, loadAbs]);

  // Switch to kalender when MA is selected from team tab
  const handleSelectMa = (id) => {
    setSelectedMa(id);
    if (id && tab === 'team') setTab('kalender');
  };

  return (
    <div className="p-page">
      <PageHeader title="Workforce" subtitle={t('workforce.subtitle')} className="mb-6" />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-surface-card border border-line rounded-card p-1 w-fit">
        {tabsDef.map((tabItem) => (
          <button key={tabItem.key} onClick={() => setTab(tabItem.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === tabItem.key ? 'bg-surface-active text-content' : 'text-[var(--text-secondary)] hover:text-content-secondary'}`}
          >{t(tabItem.labelKey)}</button>
        ))}
      </div>

      {tab === 'team' && <TeamTab mitarbeiter={mitarbeiter} absences={absences} selectedMa={selectedMa} setSelectedMa={handleSelectMa} />}
      {tab === 'kalender' && <KalenderTab mitarbeiter={mitarbeiter} absences={absences} loadAbsences={loadAbs} selectedMa={selectedMa} setSelectedMa={setSelectedMa} />}
      {tab === 'kapazitaet' && <KapazitaetTab mitarbeiter={mitarbeiter} absences={absences} />}
    </div>
  );
}
